/* eslint-disable tailwindcss/no-custom-classname */

import IconChevronEndLeft from '@apollo/icons/large/IconChevronEndLeft.svg';
import IconChevronEndRight from '@apollo/icons/large/IconChevronEndRight.svg';
import IconChevronLeft from '@apollo/icons/large/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/large/IconChevronRight.svg';
import { PropsOf, chakra, forwardRef, useStyleConfig } from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import { useDevWarning } from '../../hooks/useDevWarning';
import { formatNumber } from '../../utils/formatUtils';
import { OmitChakraProps } from '../../utils/typeUtils';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup';
import { IconButton } from '../IconButton/IconButton';
import { Tooltip } from '../Tooltip/Tooltip';

export interface PaginationProps extends OmitChakraProps<PropsOf<'div'>> {
  className?: string;
  // Current page user is on. Should be >= 1.
  currentPage: number;
  // Total count of items
  totalCount: number;
  // How many items allowed per page
  pageSize: number;
  // Gets called when pagination buttons are clicked (Next, Previous, First, Last)
  onChangePage: (page: number) => void;
  // If true, pagination buttons will be disabled (can be used for loading states or when refetching)
  isDisabled?: boolean;
}

const getNumberOfPages = (pageSize: number, totalCount: number) => {
  return Math.ceil(totalCount / pageSize);
};

export const Pagination = forwardRef<PaginationProps, 'div'>(
  (
    {
      className,
      currentPage,
      totalCount,
      pageSize,
      isDisabled,
      onChangePage,
      ...rest
    },
    ref,
  ) => {
    const styles = useStyleConfig('Pagination');

    useDevWarning('Pagination', currentPage >= 1, '`currentPage` must be >= 1');

    const numPages = getNumberOfPages(pageSize, totalCount);

    // The following indexes assume the currentPage will be >= 1;
    const startIndex =
      currentPage === 1 ? 1 : pageSize * currentPage - (pageSize - 1);
    const endIndex = Math.min(pageSize * currentPage, totalCount);

    return (
      <chakra.div
        ref={ref}
        __css={styles}
        className={classnames('orbit-pagination__container', className)}
        {...rest}
      >
        <ButtonGroup className="p-1">
          <Tooltip label="First">
            <IconButton
              size="md"
              variant="hidden"
              onClick={() => {
                onChangePage(1);
              }}
              // Don't allow the user to paginate to the previous page if
              // they're already on the first page
              isDisabled={isDisabled || currentPage === 1}
              icon={<IconChevronEndLeft />}
              aria-label="First page"
            />
          </Tooltip>
          <Tooltip label="Previous">
            <IconButton
              size="md"
              variant="hidden"
              onClick={() => {
                onChangePage(currentPage - 1);
              }}
              // Don't allow the user to paginate to the previous page if
              // they're already on the first page
              isDisabled={isDisabled || currentPage === 1}
              icon={<IconChevronLeft />}
              aria-label="Previous page"
            />
          </Tooltip>
        </ButtonGroup>
        <div>
          <span className="font-semibold text-primary">
            {startIndex}-{endIndex}
          </span>
          <span className="text-primary"> of {formatNumber(totalCount)}</span>
        </div>
        <ButtonGroup className="p-1">
          <Tooltip label="Next">
            <IconButton
              size="md"
              variant="hidden"
              onClick={() => {
                onChangePage(currentPage + 1);
              }}
              // Don't allow the user to paginate to the next page if they're
              // already on the last page
              isDisabled={isDisabled || currentPage === numPages}
              icon={<IconChevronRight />}
              aria-label="Next page"
            />
          </Tooltip>
          <Tooltip label="Last">
            <IconButton
              size="md"
              variant="hidden"
              onClick={() => {
                onChangePage(numPages);
              }}
              // Don't allow the user to paginate to the next page if they're
              // already on the last page
              isDisabled={isDisabled || currentPage === numPages}
              icon={<IconChevronEndRight />}
              aria-label="Last page"
            />
          </Tooltip>
        </ButtonGroup>
      </chakra.div>
    );
  },
);
